/**
 * Expense Repository (Refactored)
 *
 * This is a facade that provides backward compatibility while delegating
 * to specialized modules:
 * - ExpenseQueryRepository: Read operations
 * - ExpenseCommandRepository: Write operations
 * - ExpenseStatisticsService: Statistics & analytics
 * - ExpenseWorkflowService: Business logic & workflows
 *
 * This refactoring improves maintainability by separating concerns and
 * reducing file size from 497 lines to ~180 lines per module.
 */

import { BaseRepository, RepositoryResult, PaginatedResult, QueryOptions, PaginationParams } from './BaseRepository';
import { Expense } from '@/shared/types';

// Import specialized modules
import { expenseQueryRepository } from './ExpenseQueryRepository';
import { expenseCommandRepository } from './ExpenseCommandRepository';
import { expenseStatisticsService } from './ExpenseStatisticsService';
import type { ExpenseSummary } from './ExpenseStatisticsService';
import { expenseWorkflowService } from './ExpenseWorkflowService';

/**
 * Expense Repository (Facade)
 *
 * Provides a unified interface to expense operations while delegating
 * to specialized modules. This maintains backward compatibility with
 * existing code while improving code organization.
 *
 * @deprecated Prefer using the specialized modules directly:
 * - Use expenseQueryRepository for read operations
 * - Use expenseCommandRepository for write operations
 * - Use expenseStatisticsService for statistics
 * - Use expenseWorkflowService for business logic
 */
export class ExpenseRepository extends BaseRepository<Expense> {
  protected tableName = 'expenses' as const;

  // ============================================================================
  // QUERY OPERATIONS (delegated to ExpenseQueryRepository)
  // ============================================================================

  /**
   * Find all expenses for a user with pagination and filtering
   */
  async findAll(
    userId: string,
    options?: QueryOptions & PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    return expenseQueryRepository.findAll(userId, options);
  }

  /**
   * Find a single expense by ID
   */
  async findById(id: string): Promise<RepositoryResult<Expense>> {
    return expenseQueryRepository.findById(id);
  }

  /**
   * Find expenses by user ID (returns array, not paginated)
   */
  async findByUserId(userId: string): Promise<RepositoryResult<Expense[]>> {
    return expenseQueryRepository.findByUserId(userId);
  }

  /**
   * Get expenses by category
   */
  async getByCategory(
    userId: string,
    category: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    return expenseQueryRepository.getByCategory(userId, category, options);
  }

  /**
   * Get expenses by date range
   */
  async getByDateRange(
    userId: string,
    startDate: string,
    endDate: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    return expenseQueryRepository.getByDateRange(userId, startDate, endDate, options);
  }

  /**
   * Get expenses by client
   */
  async getByClient(
    userId: string,
    clientId: string,
    options?: PaginationParams
  ): Promise<RepositoryResult<PaginatedResult<Expense>>> {
    return expenseQueryRepository.getByClient(userId, clientId, options);
  }

  /**
   * Check if expense has dependencies
   */
  async hasDependencies(id: string): Promise<RepositoryResult<boolean>> {
    return expenseWorkflowService.hasDependencies(id);
  }

  // ============================================================================
  // COMMAND OPERATIONS (delegated to ExpenseCommandRepository)
  // ============================================================================

  /**
   * Create a new expense
   */
  async create(userId: string, expenseData: Omit<Expense, 'id' | 'createdAt'>): Promise<RepositoryResult<Expense>> {
    return expenseCommandRepository.create(userId, expenseData);
  }

  /**
   * Update an existing expense
   */
  async update(id: string, expenseData: Partial<Expense>): Promise<RepositoryResult<Expense>> {
    return expenseCommandRepository.update(id, expenseData);
  }

  /**
   * Delete an expense
   */
  async delete(id: string): Promise<RepositoryResult<void>> {
    return expenseCommandRepository.delete(id);
  }

  // ============================================================================
  // STATISTICS OPERATIONS (delegated to ExpenseStatisticsService)
  // ============================================================================

  /**
   * Get expense summary for dashboard
   */
  async getSummary(userId: string): Promise<RepositoryResult<ExpenseSummary>> {
    return expenseStatisticsService.getSummary(userId);
  }

  // ============================================================================
  // RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
  // ============================================================================

  /**
   * Expense Query Repository
   * Use this for read-only operations on expenses
   */
  static Query = expenseQueryRepository;

  /**
   * Expense Command Repository
   * Use this for write operations on expenses
   */
  static Command = expenseCommandRepository;

  /**
   * Expense Statistics Service
   * Use this for statistical and analytical operations
   */
  static Statistics = expenseStatisticsService;

  /**
   * Expense Workflow Service
   * Use this for business logic and workflow operations
   */
  static Workflow = expenseWorkflowService;
}

// Export singleton instance for backward compatibility
export const expenseRepository = new ExpenseRepository();

// ============================================================================
// RE-EXPORT SPECIALIZED MODULES FOR DIRECT USE
// ============================================================================

/**
 * Expense Query Repository
 * Use this for read-only operations on expenses
 */
export { expenseQueryRepository };

/**
 * Expense Command Repository
 * Use this for write operations on expenses
 */
export { expenseCommandRepository };

/**
 * Expense Statistics Service
 * Use this for statistical and analytical operations
 */
export {
  expenseStatisticsService,
  ExpenseSummary,
  // Also export additional types from ExpenseStatisticsService
  type ExpensePerformanceMetrics,
  type ExpenseTrend,
};

// Import types from ExpenseStatisticsService for re-export
import type { ExpensePerformanceMetrics, ExpenseTrend } from './ExpenseStatisticsService';

/**
 * Expense Workflow Service
 * Use this for business logic and workflow operations
 */
export { expenseWorkflowService };
